import { findIndexedParentUserMessage } from '@/lib/messageIndex';

export const BAD_INFERENCE_JOB_STATUSES = new Set([
  'pending',
  'in_progress',
  'processing',
  'canceled',
  'cancelled',
  'failed',
]);

// True when the inferences array contains at least one job in a "still working / failed"
// state. Used to decide whether a message bubble should remain visible (because there's
// an in-progress card to render) and whether locally-held inferences are "more truthful"
// than an empty/missing payload from the server.
export function hasPendingInferenceJobs(inferences) {
  if (!Array.isArray(inferences) || inferences.length === 0) return false;
  return inferences.some((inf) =>
    Array.isArray(inf?.output?.jobs) &&
    inf.output.jobs.some((job) => BAD_INFERENCE_JOB_STATUSES.has(job?.status))
  );
}

// Choose the authoritative `inferences` array between what we already have locally and
// what the server just sent. The server is authoritative ONLY when it actually returns
// inferences with jobs. In a streaming/poll race the server can momentarily echo back
// `inferences: []` for a message whose backend job is still pending — if we naively
// shallow-merge that empty array over our local pending-job state, the in-progress
// card vanishes from the UI even though the job is still running. So:
//   - server has any inference with any job  → trust the server
//   - otherwise local has pending jobs       → keep the local copy
//   - otherwise (both empty / both done)     → trust the server
export function pickAuthoritativeInferences(localInferences, incomingInferences) {
  const incomingHasJobs = Array.isArray(incomingInferences) && incomingInferences.some(
    (inf) => Array.isArray(inf?.output?.jobs) && inf.output.jobs.length > 0
  );
  if (incomingHasJobs) return incomingInferences;
  if (hasPendingInferenceJobs(localInferences)) return localInferences;
  return incomingInferences;
}

function messageTextLength(value) {
  return typeof value === 'string' ? value.length : 0;
}

function messageTextIsEmpty(value) {
  return typeof value !== 'string' || value.trim().length === 0;
}

/**
 * Choose between locally-streamed text and a REST/socket snapshot.
 *
 * Tokens live on the client until persist catches up. A detail fetch or
 * `conversation_stream_end` payload can therefore arrive with empty (or
 * truncated) `content` / `thinkingContent` after the UI already rendered
 * the full reply. Naive `{...incoming}` then makes `shouldDisplayMessage`
 * false and the bubble vanishes — web and native, intermittent on timing.
 *
 * Keep local text when incoming is empty. Also keep it when incoming is a
 * strict prefix of local (mid-stream persist). Real edits and regenerations
 * are not prefixes, so those still take the incoming value.
 */
export function pickAuthoritativeMessageText(localText, incomingText) {
  if (messageTextIsEmpty(incomingText) && !messageTextIsEmpty(localText)) {
    return localText;
  }
  if (
    messageTextLength(localText) > messageTextLength(incomingText)
    && typeof localText === 'string'
    && typeof incomingText === 'string'
    && localText.startsWith(incomingText)
  ) {
    return localText;
  }
  return incomingText;
}

// Map a requested-media kind to file extensions/keywords we recognize as the
// produced output for that kind. Keep this in sync with the media types that
// `isRequestingMedia` can carry (image / video / audio / 3d).
const MEDIA_TYPE_EXTENSIONS = {
  image: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'tiff', 'tif', 'heic', 'heif'],
  video: ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', 'mpg', 'mpeg'],
  audio: ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg', 'opus', 'oga', 'webm'],
  '3d': ['glb', 'gltf', 'obj', 'fbx', 'stl', 'usdz', 'ply'],
};

const MEDIA_TYPE_MIMES = {
  image: ['image/'],
  video: ['video/'],
  audio: ['audio/'],
  '3d': ['model/'],
};

function _matchesMediaKind(file, mediaType) {
  if (!file || typeof file !== 'object') return false;
  const fileType = String(file.fileType || '').toLowerCase();
  if (fileType === mediaType) return true;

  const type = String(file.type || '').toLowerCase();
  if (type === mediaType) return true;

  const mimePrefixes = MEDIA_TYPE_MIMES[mediaType] || [];
  if (mimePrefixes.some((p) => type.startsWith(p))) return true;

  const mimeType = String(file.mimeType || '').toLowerCase();
  if (mimePrefixes.some((p) => mimeType.startsWith(p))) return true;

  const extensions = MEDIA_TYPE_EXTENSIONS[mediaType] || [];
  const extension = String(file.extension || '').toLowerCase().replace(/^\./, '');
  if (extension && extensions.includes(extension)) return true;

  const name = String(file.originalName || file.name || '').toLowerCase();
  if (!name || extensions.length === 0) return false;
  const escaped = extensions.map((e) => e.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  return new RegExp(`\\.(${escaped})$`).test(name);
}

// Returns true when the file looks like a generated image. Kept for backward
// compatibility with existing call sites — prefer `isMediaOutputFile` for new
// code that needs to handle video/audio/3d too.
export function isImageOutputFile(file) {
  return _matchesMediaKind(file, 'image');
}

// Returns true when `file` is a generated artifact of the requested kind.
// `mediaType` should be one of: 'image' | 'video' | 'audio' | '3d'. When the
// kind is unknown/unspecified, treats any of the four as a match so the
// caller can fall back to a generic "any media" check.
export function isMediaOutputFile(file, mediaType) {
  if (!file || typeof file !== 'object') return false;
  if (!mediaType) {
    return _matchesMediaKind(file, 'image')
      || _matchesMediaKind(file, 'video')
      || _matchesMediaKind(file, 'audio')
      || _matchesMediaKind(file, '3d');
  }
  return _matchesMediaKind(file, String(mediaType).toLowerCase());
}

// Returns true when the message already has an output file of the given media
// kind attached (legacy `images` array also counts for 'image'). Pass no
// `mediaType` to check whether the message carries any generated media file.
export function messageHasMediaOutput(message, mediaType) {
  if (!message) return false;
  const kind = mediaType ? String(mediaType).toLowerCase() : null;
  if ((kind === 'image' || !kind)
    && Array.isArray(message.images) && message.images.length > 0) {
    return true;
  }
  if (!Array.isArray(message.files) || message.files.length === 0) return false;
  return message.files.some((file) => isMediaOutputFile(file, kind));
}

// Back-compat alias. Existing call sites that specifically want to check for an
// image output can keep using this name; new code should prefer
// `messageHasMediaOutput` so video/audio/3d generations are handled too.
export function messageHasImageOutput(message) {
  return messageHasMediaOutput(message, 'image');
}

// Media kinds that we render in the chat as standalone visual cards. PDF /
// document generations finish as a downloadable file card and the regular text
// stream handles the "waiting" UX, so they don't get the dedicated media
// placeholder treatment here.
const RENDERED_MEDIA_KINDS = new Set(['image', 'video', 'audio', '3d']);

function _normalizedMediaKind(message) {
  const raw = String(message?.isRequestingMedia || '').toLowerCase();
  if (!raw) return null;
  if (RENDERED_MEDIA_KINDS.has(raw)) return raw;
  return null;
}

// Returns true when any inference attached to the message has at least one
// job in a terminal-failure state (failed / cancelled). This signals that
// the async media generation is OVER but unsuccessful — the message itself
// doesn't carry isError/hasError in this case (the inference job does), so
// callers must check both before deciding the request is still "in flight".
function _hasTerminallyFailedInferenceJob(message) {
  if (!message || !Array.isArray(message.inferences)) return false;
  return message.inferences.some((inf) => {
    const jobs = inf?.output?.jobs;
    if (!Array.isArray(jobs)) return false;
    return jobs.some((job) => {
      const status = String(job?.status || '').toLowerCase();
      return status === 'failed' || status === 'cancelled';
    });
  });
}

// True while the assistant message is waiting for an async media artifact
// (image, video, song, 3D model) to be attached. This is the cross-cutting
// guard that prevents the loading UI from vanishing the moment
// `conversation_stream_end` clears the streaming flags but BEFORE the
// generated file is wired onto the message — without it, the chat goes blank
// for 5s-2min until the file finally shows up (or the user refreshes).
//
// IMPORTANT: must also fall through (return false) when the backend reports
// a terminal job failure. Fal / Veo / minimax errors land on the inference
// job's status, NOT on the message's isError/hasError flag — so without the
// inference-job check the placeholder spinner sticks around forever (next
// to the proper "Failed" card) and the user thinks generation is still
// running even after we've shown them the failure UI.
export function isAwaitingRequestedMedia(message) {
  if (!message) return false;
  const kind = _normalizedMediaKind(message);
  if (!kind) return false;
  if (messageHasMediaOutput(message, kind)) return false;
  if (message.isError || message.hasError) return false;
  if (_hasTerminallyFailedInferenceJob(message)) return false;
  return true;
}

/**
 * Derives the `isWaiting` flag to pass to MessageContent, with special handling
 * for messages that are generating media (images, videos, songs, 3D models).
 *
 * For media-requesting messages, we want the loading UI to stay visible until
 * the media actually arrives or the message errors out. This guards against
 * socket events (e.g. early `conversation_stream_end` from the text side of
 * the inference, or async post-processors that haven't finished saving the
 * generated file yet) that flip `message.isWaiting` to false before the media
 * lands — which would otherwise make the user think generation failed.
 */
export function computeImageAwareIsWaiting(message) {
  if (!message) return false;

  if (isAwaitingRequestedMedia(message)) return true;

  if (message.isRequestingMedia) return !!message.isWaiting;
  // Keep the waiting/processing indicator during CoT so we don't swap to a
  // separate thinking panel mid-stream (that flicker is what users see as
  // "processing" ↔ "thinking"). Traces render only after inference ends.
  if (message.isThinking) return true;
  return !!message.isWaiting;
}

// Hide timestamps under the thinking/waiting placeholder. A lone timestamp next
// to empty/dots content reads as a layout bug on native (WIDE especially).
export function shouldShowMessageTimestamp(message, { isUser, showTimestamps } = {}) {
  if (!showTimestamps || !message?.timestamp) return false;
  if (!isUser && computeImageAwareIsWaiting(message)) return false;
  if (!isUser && (message.isStreaming || message.isTyping) && !String(message.content || '').trim()) {
    return false;
  }
  return true;
}

export function getMessageDisplayInfo(message, isUser, opts = {}) {
  const ignoreFiles = Boolean(opts?.ignoreFiles);
  const hasContent = Boolean(message?.content && message.content.length > 0);
  const hasThinkingContent = Boolean(message?.thinkingContent && message.thinkingContent.length > 0);
  const hasImages = Array.isArray(message?.images) && message.images.length > 0;
  const hasFiles = !ignoreFiles && Array.isArray(message?.files) && message.files.length > 0;

  const hasAnyInferencesWithIncompleteJobs = !isUser && Array.isArray(message?.inferences) && message.inferences.some((inf) => Array.isArray(inf?.output?.jobs) && inf.output.jobs.some((job) => BAD_INFERENCE_JOB_STATUSES.has(job?.status)));

  // Keep the bubble visible while we're still waiting for a requested media
  // artifact to be attached. Without this guard `conversation_stream_end`
  // clears the streaming flags BEFORE the generated file is wired onto the
  // message, which makes the bubble disappear entirely until the file finally
  // shows up (often 30s-2min later via job polling or a page refresh) —
  // exactly the "everything went blank in the middle of generation" bug.
  const isWaitingForRequestedMedia = !isUser && isAwaitingRequestedMedia(message);

  const shouldDisplayMessage = hasContent || hasThinkingContent || hasFiles || hasImages || message?.isWaiting || message?.isTyping || message?.isThinking || message?.isStreaming || message?.isError || message?.hasError || hasAnyInferencesWithIncompleteJobs || isWaitingForRequestedMedia;

  return { shouldDisplayMessage, hasAnyInferencesWithIncompleteJobs, badStatuses: BAD_INFERENCE_JOB_STATUSES, isWaitingForRequestedMedia };
}

/**
 * Parent user turn for an assistant message. Prefers `parentMessageId`, but
 * walks backward when that id is missing, stale (optimistic vs server id),
 * or points at a non-user message — otherwise Auto-mode attribution silently
 * fails and the model avatar leaks into the footer.
 */
export function findParentUserMessage(messages, message) {
  if (!message || message.role === 'user' || !Array.isArray(messages) || messages.length === 0) {
    return null;
  }

  return findIndexedParentUserMessage(messages, message);
}

/** True when the composer sent with no explicit model (Auto). */
export function isAutoModeSend(formattedMessage) {
  return !Array.isArray(formattedMessage?.modelIds) || formattedMessage.modelIds.length === 0;
}

/** Hide routed-model attribution only when this specific turn was Auto. */
export function isAutoModeAttribution(parentUserMessage, message) {
  return parentUserMessage?.parameters?.___isAutoMode === true
    || message?.parameters?.___isAutoMode === true;
}

function firstNonEmptyIdList(...candidates) {
  for (const ids of candidates) {
    if (Array.isArray(ids) && ids.length > 0) return ids;
  }
  return null;
}

/**
 * Footer / model-card IDs for an assistant turn.
 * Manual Arya can be routed to a different inference model (political intent);
 * a 1-id `___displayModelIds` keeps the user's pick so the card does not switch.
 * Multi-model comparison stamps the full selection on the user turn — that list
 * is not this card's identity, so we keep the assistant's own modelIds.
 * Auto turns return [] so the routed model stays hidden.
 */
export function resolveAttributionModelIds(message, parentUserMessage, isAutoMode) {
  if (isAutoMode) return [];
  const inferenceIds = extractMessageModelIds(message);
  // Only a single display id is a 1:1 override (Arya → Gemini). A comparison
  // list would make every response card show the first selected model.
  const displayOverride = firstNonEmptyIdList(
    singleIdList(message?.parameters?.___displayModelIds),
    singleIdList(parentUserMessage?.parameters?.___displayModelIds),
  );
  return displayOverride || inferenceIds;
}

function singleIdList(ids) {
  return Array.isArray(ids) && ids.length === 1 ? ids : null;
}

/**
 * Parameters to persist on the optimistic user bubble. Auto mode is decided
 * server-side, but the footer hides the model avatar from `___isAutoMode` on
 * this client message — stamp it locally so follow-ups don't flash the card
 * before `new_user_message` echoes the server flag.
 *
 * Manual selections also stamp `___displayModelIds` so Arya (and any other
 * picked model) stays on the response card when the intent engine later
 * swaps `message.modelIds` for inference.
 */
export function parametersForOptimisticUserMessage(formattedMessage, composerState) {
  const base = (formattedMessage?.parameters && typeof formattedMessage.parameters === 'object')
    ? formattedMessage.parameters
    : {};
  // Some native paths resolve a platform-default model before the socket send,
  // so modelIds is non-empty even though the user explicitly selected Auto.
  // Stamp that fact onto the turn now; historical rendering must never infer
  // attribution from the composer's later state.
  const isAuto = isAutoModeSend(formattedMessage)
    || composerState?.userExplicitAutoMode === true;
  const selectedModelIds = Array.isArray(formattedMessage?.modelIds)
    ? formattedMessage.modelIds.filter(Boolean)
    : [];
  const hasDisplayIds = Array.isArray(base.___displayModelIds) && base.___displayModelIds.length > 0;
  const withDisplay = (!isAuto && selectedModelIds.length > 0 && !hasDisplayIds)
    ? { ...base, ___displayModelIds: selectedModelIds }
    : base;
  if (!isAuto) {
    return Object.keys(withDisplay).length > 0 ? withDisplay : undefined;
  }
  return { ...withDisplay, ___isAutoMode: true };
}

/** Merge server echo parameters over the optimistic bubble without dropping client-only flags. */
export function mergeUserMessageParameters(localParameters, serverParameters) {
  if (!serverParameters || typeof serverParameters !== 'object') {
    return localParameters;
  }
  const merged = { ...(localParameters || {}), ...serverParameters };
  // Server echo is emitted before political routing stamps ___displayModelIds.
  // Keep the client's manual-selection IDs unless the server already has them.
  const localDisplay = localParameters?.___displayModelIds;
  const serverDisplay = serverParameters?.___displayModelIds;
  if (
    Array.isArray(localDisplay) && localDisplay.length > 0 &&
    !(Array.isArray(serverDisplay) && serverDisplay.length > 0)
  ) {
    merged.___displayModelIds = localDisplay;
  }
  return merged;
}

/** Coerce a model ref (id string, ObjectId, or `{ id|_id }`) to a store key. */
export function normalizeModelId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    const nested = value.id ?? value._id;
    if (nested != null && nested !== value) return normalizeModelId(nested);
  }
  if (typeof value.toString === 'function') {
    const asString = value.toString();
    if (asString && asString !== '[object Object]') return asString;
  }
  return null;
}

/**
 * IDs for a message header. Handles serialized `{ id }`, mongoose `{ _id }`,
 * raw ObjectId strings, and the live `modelIds` field from socket events.
 */
export function extractMessageModelIds(msg) {
  if (!msg) return [];
  if (Array.isArray(msg.modelIds) && msg.modelIds.length > 0) {
    const fromIds = msg.modelIds.map(normalizeModelId).filter(Boolean);
    if (fromIds.length > 0) return fromIds;
  }
  if (Array.isArray(msg.models) && msg.models.length > 0) {
    return msg.models.map(normalizeModelId).filter(Boolean);
  }
  return [];
}

/**
 * Which model(s) a reply should show.
 * A 1:1 political/intent swap keeps the user's pick. A fan-out card keeps the
 * model that actually produced that reply — never the full selected-model list.
 */
export function resolveDisplayModelIds({
  isAutoMode = false,
  messageModelIds,
  displayOverrideIds,
} = {}) {
  if (isAutoMode) return [];
  const own = (messageModelIds || []).map(normalizeModelId).filter(Boolean);
  const override = (displayOverrideIds || []).map(normalizeModelId).filter(Boolean);
  if (override.length === 1 && own.length <= 1) return override;
  if (own.length === 1) return own;
  if (own.length === 0 && override.length > 0) return override;
  return own;
}

export function mapSerializedMessageToMessage(msg) {
  if (!msg) return {};
  
  // Handle both id and _id (MongoDB uses _id, but frontend expects id)
  // Generate a fallback ID if neither is present to prevent rendering issues
  const messageId = msg.id || msg._id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  
  // Get actions from parameters and existing actions, combine uniquely
  const paramActions = msg?.parameters?.___actions || [];
  const existingActions = msg?.actions || [];
  const uniqueActions = [...new Set([...paramActions, ...existingActions])];
  const isWorkModePlusRequired = msg?.parameters?.___workModePlusRequired === true;
  const workModePlusButtons = [{
    action: 'go-plus',
    title: 'Upgrade to Plus',
    data: {
      feature: 'work_mode',
      title: 'This task is built for Work mode',
      subtitle: 'Work mode can plan, research, use tools and files, and deliver finished work. It is included with Gab AI Plus.',
    },
  }];

  // Get inferences here
  const inferences = (msg?.inferences || []).map((inference) => {
    const jobs = (inference?.output?.jobs || []).map((job) => {
      return {
        id: job.id,
        status: job.status,
        type: job.type,
        updatedAt: job.updatedAt,
        createdAt: job.createdAt,
        progress: job.progress,
        error: {
          message: job.error?.message || null,
          code: job.error?.code || null,
        },
        owner: {
          entityId: inference?.initiators?.owner?.entityId || '',
          entityType: inference?.initiators?.owner?.entityType || '',
        },
      };
    });

    return {
      id: inference.id,
      source: inference.source,
      status: inference.status,
      updatedAt: inference.updatedAt,
      createdAt: inference.createdAt,
      output: {
        jobs: jobs,
      },
      metrics: {
        totalDurationMS: inference?.metrics?.totalDurationMS || 0,
      },
      creditCost: {
        allocations: inference?.creditCost?.allocations || [],
        breakdown: {
          baseCost: inference?.creditCost?.breakdown?.baseCost || 0,
          contextMultiplier: inference?.creditCost?.breakdown?.contextMultiplier || 0,
          parameterCost: inference?.creditCost?.breakdown?.parameterCost || 0,
        },
        contextTokens: inference?.creditCost?.contextTokens || 0,
        isMonthlyAllotment: inference?.creditCost?.isMonthlyAllotment || false,
        isRefunded: inference?.creditCost?.isRefunded || false,
        isDeferred: inference?.creditCost?.isDeferred || false,
        creditsReserved: inference?.creditCost?.creditsReserved || false,
        reservationPending: inference?.creditCost?.reservationPending || false,
        refundPending: inference?.creditCost?.refundPending || false,
        reconciliation: inference?.creditCost?.reconciliation,
        total: inference?.creditCost?.total ?? null,
      },
      initiators: {
        conversation: inference?.initiators?.conversation || '',
        messageId: inference?.initiators?.messageId || '',
        inferenceTaskId: inference?.initiators?.inferenceTask || '',
        owner: {
          entityId: inference?.initiators?.owner?.entityId || '',
          entityType: inference?.initiators?.owner?.entityType || '',
        },
      },
    };
  });
  
  // Only include images if they have content to avoid overwriting local images
  // that may have been set by assistant_message_image_stream
  const hasBackendImages = Array.isArray(msg.images) && msg.images.length > 0;
  const hasBackendFiles = Array.isArray(msg.files) && msg.files.length > 0;
  
  return {
    id: messageId,

    actions: uniqueActions.length > 0 ? uniqueActions : undefined,
    agentIds: msg?.agents?.map(a => a.id) || [],
    buttons: isWorkModePlusRequired ? workModePlusButtons : (msg.buttons || []),
    citations: msg.citations || [],
    interactiveImageAssets: msg.interactiveImageAssets || [],
    products: msg.products || [],
    stockQuotes: msg.stockQuotes || [],
    places: msg.places || [],
    map: msg.map || null,
    content: msg.content,
    reactions: msg.reactions || {},
    ...(hasBackendFiles && { files: msg.files }),
    flags: msg.flags,
    // Only include images if backend actually has them, otherwise let local state persist
    ...(hasBackendImages && { images: msg.images }),
    inferences: inferences || [],                
    modelIds: extractMessageModelIds(msg),
    parameters: msg.parameters || undefined,
    parentMessageId: msg.parentMessageId,
    quoteContent: msg.quoteContent || '',
    role: msg.role === 'assistant' ? 'assistant' : 'user',
    thinkingContent: msg.thinkingContent || '',
    thinkingDurationMs: msg.thinkingDurationMs || 0,
    timestamp: msg.date,

    isWaiting: msg.isWaiting || false,
    isTyping: msg.isTyping || false,
    isThinking: msg.isThinking || false,
    isStreaming: msg.isStreaming || false,
    isError: msg.isError || isWorkModePlusRequired || false,
    errorType: isWorkModePlusRequired ? 'WORK_MODE_PLUS_REQUIRED' : msg.errorType,
    // Backend persists terminal failures (e.g. failed image generation) on
    // flags.hasError — surface it top-level so error cards survive a refresh.
    hasError: msg.hasError || msg.flags?.hasError || false,
    isTruncated: msg.isTruncated || false,
    stopReason: msg.stopReason || null,
    isEdited: msg.isEdited || false,
    editHistory: msg.editHistory || [],

    // Preserve image model flags if present
    ...(msg.isImage && { isImage: msg.isImage }),
    ...(msg.isRequestingMedia && { isRequestingMedia: msg.isRequestingMedia }),
  };
}
